import React, { CSSProperties } from 'react';
import './Countdown.css';
import FlipTimer from './FlipTimer';
import TextFill, { getTextFill } from '../text/TextFill';
import notchedBox from './assets/timer-notched-box.svg';
import { cssUrl } from '../../../stickyBanner.helper';
import {
	alignToFlex,
	getMarginStyle,
	getPaddingStyle,
	getTextStyle,
} from '../styles';
import { TimeLeft } from '../../../stickyBanner.helper';
import { ComponentSettings, TypographySettings } from '../../../types';

interface CountdownProps {
	settings: ComponentSettings;
	label: TypographySettings;
	digits: TypographySettings;
	timeLeft: TimeLeft;
}

const BOX_UNITS = [
	{ key: 'days', label: 'DAYS' },
	{ key: 'hours', label: 'HRS' },
	{ key: 'minutes', label: 'MIN' },
	{ key: 'seconds', label: 'SEC' },
] as const;

/**
 * Each digit in a fixed-width cell, so the timer does not change width
 * (and move the banner) every second, whatever the font's digit widths.
 *
 * @param value Digits, e.g. "07".
 */
const renderDigits = ( value: string ) =>
	value.split( '' ).map( ( digit, index ) => (
		<span key={ index } className="sb-countdown-digit">
			{ digit }
		</span>
	) );

/**
 * Live countdown. Variants: text, textlight, textnavy (same layout,
 * different presets), notched (boxes), flip (tiles), boxes (with units).
 */
const Countdown: React.FC< CountdownProps > = ( {
	settings,
	label,
	digits,
	timeLeft,
} ) => {
	const { variant, style } = settings;

	let timer: React.ReactNode;
	if ( variant === 'flip' ) {
		timer = (
			<FlipTimer
				timeLeft={ timeLeft }
				tileColor={ style.tileColor }
				digitColor={ style.digitColor }
				unitColor={ style.unitColor }
			/>
		);
	} else if ( variant === 'notched' ) {
		// Figma "Limited Time Offer" timer: four notched boxes, no units.
		const values = [
			timeLeft.days.padStart( 2, '0' ),
			timeLeft.hoursOfDay,
			timeLeft.minutes,
			timeLeft.seconds,
		];
		timer = (
			<div
				className="sb-countdown-notched"
				role="timer"
				aria-label={ `${ values[ 0 ] } days ${ values[ 1 ] }:${ values[ 2 ] }:${ values[ 3 ] }` }
			>
				{ values.map( ( value, index ) => (
					<span
						key={ index }
						className="sb-countdown-notched-box"
						style={
							{
								...getTextStyle( digits ),
								backgroundImage: cssUrl( notchedBox ),
							} as CSSProperties
						}
					>
						<TextFill text={ digits }>
							{ renderDigits( value ) }
						</TextFill>
					</span>
				) ) }
			</div>
		);
	} else if ( variant === 'boxes' ) {
		const values = {
			days: timeLeft.days.padStart( 2, '0' ),
			hours: timeLeft.hoursOfDay,
			minutes: timeLeft.minutes,
			seconds: timeLeft.seconds,
		};
		timer = (
			<div
				className="sb-countdown-boxes"
				role="timer"
				aria-label={ `${ values.days } days ${ values.hours }:${ values.minutes }:${ values.seconds }` }
			>
				{ BOX_UNITS.map( ( unit ) => (
					<div key={ unit.key } className="sb-countdown-box">
						<span
							className="sb-countdown-box-value"
							style={
								{
									...getTextStyle( digits ),
									color: style.boxDigitColor,
									backgroundColor: style.boxColor,
								} as CSSProperties
							}
						>
							<TextFill text={ digits }>
								{ renderDigits( values[ unit.key ] ) }
							</TextFill>
						</span>
						<span
							className="sb-countdown-box-unit"
							style={ { color: style.unitColor } }
						>
							{ unit.label }
						</span>
					</div>
				) ) }
			</div>
		);
	} else {
		// More than a day left: "02 : 05 : 10 : 30" (days : hours : min : sec).
		const days = Number( timeLeft.days ) || 0;
		timer = (
			<p
				className="sb-countdown-text"
				role="timer"
				aria-label={
					days > 0
						? `${ days } days ${ timeLeft.hoursOfDay }:${ timeLeft.minutes }:${ timeLeft.seconds }`
						: `${ timeLeft.hours }:${ timeLeft.minutes }:${ timeLeft.seconds }`
				}
				style={ {
					...getTextStyle( digits ),
					...getTextFill( digits ),
					justifyContent: alignToFlex( digits.align ),
				} }
			>
				{ days > 0 && (
					<>
						{ renderDigits( String( days ) ) }
						<span className="sb-countdown-sep"> : </span>
					</>
				) }
				{ renderDigits(
					days > 0 ? timeLeft.hoursOfDay : timeLeft.hours
				) }
				<span className="sb-countdown-sep"> : </span>
				{ renderDigits( timeLeft.minutes ) }
				<span className="sb-countdown-sep"> : </span>
				{ renderDigits( timeLeft.seconds ) }
			</p>
		);
	}

	return (
		<div
			className={ `sb-countdown is-${ variant }` }
			style={ {
				...getPaddingStyle( style ),
				...getMarginStyle( style ),
			} }
		>
			{ label.isVisible && label.text && (
				<p
					className="sb-countdown-label"
					style={ {
						...getTextStyle( label ),
						alignSelf: alignToFlex( label.align ),
					} }
				>
					<TextFill text={ label }>{ label.text }</TextFill>
				</p>
			) }
			{ timer }
		</div>
	);
};

export default Countdown;
